import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Play, Pause, RotateCcw } from "lucide-react";
import { PageHeader, Tabs } from "@/components/kit";
import { BREATHING_TECHNIQUES, CATEGORIES } from "@/data/breathing";
import { HelpRating } from "@/components/playbook/HelpRating";

const phasesOf = (t) => [
  ["Breathe in", t.inhale, 1.0],
  t.hold1 ? ["Hold", t.hold1, 1.0] : null,
  ["Breathe out", t.exhale, 0.55],
  t.hold2 ? ["Hold", t.hold2, 0.55] : null,
].filter(Boolean);

const Session = ({ t, onClose }) => {
  const phases = phasesOf(t);
  const [running, setRunning] = useState(false);
  const [st, setSt] = useState({ idx: 0, left: phases[0][1], cycles: 0 });
  const timer = useRef(null);

  useEffect(() => {
    if (!running) return;
    timer.current = setInterval(() => {
      setSt((s) => {
        if (s.left > 1) return { ...s, left: s.left - 1 };
        const n = (s.idx + 1) % phases.length;
        return { idx: n, left: phases[n][1], cycles: s.cycles + (n === 0 ? 1 : 0) };
      });
    }, 1000);
    return () => clearInterval(timer.current);
  }, [running, phases]);

  const { idx, left, cycles } = st;
  const [label, dur, scale] = phases[idx];
  const reset = () => { setRunning(false); setSt({ idx: 0, left: phases[0][1], cycles: 0 }); };

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center p-6" style={{ background: "#120F1D" }} data-testid="breathing-session">
      <button onClick={onClose} className="absolute top-6 right-6 text-white/70 hover:text-white text-sm font-semibold" data-testid="breathing-close-button">Close</button>
      <h2 className="text-white text-2xl font-bold">{t.icon} {t.name}</h2>
      <p className="text-white/60 text-sm mt-1">Cycles completed: <span data-testid="breathing-cycles">{cycles}</span></p>
      <div className="relative w-72 h-72 my-12 flex items-center justify-center">
        <div className="absolute inset-0 rounded-full opacity-20" style={{ background: t.color }} />
        <div className="absolute inset-6 rounded-full flex items-center justify-center"
          style={{ background: `radial-gradient(circle, ${t.color}, ${t.color}99)`, transform: `scale(${running ? scale : 0.7})`, transition: `transform ${running ? dur : 0.6}s ease-in-out`, boxShadow: `0 0 80px ${t.color}88` }} />
        <div className="relative text-center text-white">
          <div className="text-xl font-semibold" data-testid="breathing-phase">{running ? label : "Ready"}</div>
          <div className="font-display text-6xl font-bold" data-testid="breathing-countdown">{running ? left : "—"}</div>
        </div>
      </div>
      <div className="flex gap-3">
        <button className="mm-btn" style={{ background: t.color }} onClick={() => setRunning(!running)} data-testid="breathing-toggle-button">
          {running ? <Pause size={18} /> : <Play size={18} />}{running ? "Pause" : "Start"}
        </button>
        <button className="mm-btn-ghost" onClick={reset} data-testid="breathing-reset-button"><RotateCcw size={16} /></button>
      </div>
      {cycles >= 1 && !running && <HelpRating kind="calm" toolId={t.id} toolName={t.name} dark className="justify-center mt-6" />}
      <p className="text-white/50 text-xs mt-6">{phases.map((p) => `${p[0]} ${p[1]}s`).join(" · ")}</p>
    </div>
  );
};

export default function Breathing() {
  const [cat, setCat] = useState("all");
  const [params, setParams] = useSearchParams();
  const [active, setActive] = useState(() => BREATHING_TECHNIQUES.find((t) => t.id === params.get("t")) || null);
  useEffect(() => { if (params.get("t")) setParams({}, { replace: true }); }, [params, setParams]);
  const list = BREATHING_TECHNIQUES.filter((t) => cat === "all" || t.category === cat);
  return (
    <div data-testid="breathing-page">
      <PageHeader back title="Breathing" subtitle="Guided breathwork to calm your nervous system" />
      <Tabs items={CATEGORIES} value={cat} onChange={setCat} testPrefix="breathing-cat" />
      <div className="grid sm:grid-cols-2 gap-4 mm-stagger">
        {list.map((t) => (
          <button key={t.id} onClick={() => setActive(t)} className="mm-card mm-card-hover p-5 text-left" data-testid={`breathing-technique-${t.id}`}>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-2xl" style={{ background: `${t.color}22` }}>{t.icon}</div>
              <div><div className="font-semibold">{t.name}</div><div className="text-xs text-mm-muted">{t.duration} · {t.category}</div></div>
            </div>
            <p className="text-sm text-mm-muted mt-3">{t.description}</p>
            <div className="flex flex-wrap gap-1.5 mt-3">{t.bestFor?.map((b) => <span key={b} className="text-xs px-2 py-0.5 rounded-full" style={{ background: `${t.color}1a`, color: t.color }}>{b}</span>)}</div>
          </button>
        ))}
      </div>
      {active && <Session t={active} onClose={() => setActive(null)} />}
    </div>
  );
}
